import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'
import {
  BardText,
  DrivingSchoolHeaderBlock,
  FlchHeaderBlock,
} from '../../@types/statamic'
import Container from '../../components/layout/Container'
import { PageInfo } from '@/src/@types/models'
import Bard from '@/src/components/bard/Bard'
import Picture from '@/src/components/elements/Picture'
import { Breadcrumb } from '@/src/components/flch/breadcrumbs'
import Breadcrumbs from '@/src/components/flch/components/Breadcrumbs'
import Search from '@/src/components/flch/components/Search'
import styles from '@/src/css/Masks.module.css'

export function getTitle({ title }: DrivingSchoolHeaderBlock) {
  return title
}

export function getDescription({ hero_content }: DrivingSchoolHeaderBlock) {
  const bardText = hero_content.find((bard) => bard.type === 'text') as BardText
  if (bardText) {
    const pTag = bardText.text.match(/.*?<p>(.*?)<\/p>/)
    if (pTag) {
      return pTag[1]
    }
  }
  return undefined
}

export default function Component({
  header_type,
  title,
  heading,
  hero_content,
  images,
  search,
  search_category,
  breadcrumbs,
}: FlchHeaderBlock & { pageInfo: PageInfo; breadcrumbs?: Breadcrumb[] }) {
  return (
    <div
      className={clsx('relative overflow-hidden', {
        'bg-linear-to-b from-gray-50 to-gray-100 pt-16 pb-12 xl:pt-27':
          header_type.value === 'light',
        'mt-15 py-12 text-white xl:mt-21 xl:py-16':
          header_type.value === 'background_image',
      })}
    >
      {header_type.value === 'background_image' && images.length !== 0 && (
        <>
          <div
            className="absolute inset-0 -z-10"
            style={{
              background:
                'linear-gradient(67deg, rgba(13, 11, 63, 0.98) 35%, rgba(13, 11, 63, 0.76) 62.2%, rgba(13, 11, 63, 0.58) 81.55%, rgba(13, 11, 63, 0.5) 100%)',
            }}
          />
          <Picture
            {...images[0]}
            fill
            sizes="100vw"
            className="-z-20 object-cover grayscale"
          />
        </>
      )}
      <Container fullPageOnMobile>
        <div
          className={clsx('grid', {
            'md:min-h-140 md:grid-cols-2 md:gap-16':
              header_type.value === 'light' && images.length !== 0,
            'max-w-208': !(
              header_type.value === 'light' && images.length !== 0
            ),
          })}
        >
          <div
            className={clsx(
              '@container order-2 flex w-full flex-col justify-center px-5 md:order-1 md:px-0',
              {
                'pt-8 md:pt-0': header_type.value === 'light',
              }
            )}
          >
            {breadcrumbs && breadcrumbs.length > 1 ? (
              <>
                <Breadcrumbs items={breadcrumbs} />
                <h1
                  className={clsx(
                    heading
                      ? 'mt-8 font-bold md:mt-10'
                      : 'mt-8 text-7xl font-black text-balance md:mt-10 md:text-25xl'
                  )}
                >
                  {title}
                </h1>
              </>
            ) : (
              <h1
                className={clsx({
                  'text-7xl font-black text-balance md:text-25xl': !heading,
                  'font-bold': !!heading,
                })}
              >
                {title}
              </h1>
            )}
            {heading && (
              <h2
                className={clsx(
                  'text-7xl font-black text-balance md:text-25xl',
                  breadcrumbs && breadcrumbs.length > 1 ? 'mt-1' : 'mt-3'
                )}
              >
                {heading}
              </h2>
            )}
            <Bard
              content={hero_content}
              textColor={
                header_type.value === 'background_image' ? 'white' : undefined
              }
              className="mt-5 md:mt-6"
              fullWidth
            />
            {search && (
              <div className="mt-8">
                <Search defaultCategory={search_category?.value} />
              </div>
            )}
          </div>
          {header_type.value === 'light' && images.length !== 0 && (
            <div className="order-1 md:order-2">
              <ImageCarousel images={images} />
            </div>
          )}
        </div>
      </Container>
    </div>
  )
}

const ImageCarousel = ({
  images,
}: {
  images: DrivingSchoolHeaderBlock['images']
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
  })

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  return (
    <div
      className="relative aspect-[3/2] overflow-hidden md:aspect-auto md:h-140 md:w-210 md:rounded-2xl md:shadow-sm"
      ref={emblaRef}
    >
      <ul className="flex h-full select-none">
        {images.map((image, index) => (
          <li key={index} className="relative min-w-0 flex-[0_0_100%]">
            <Picture
              {...image}
              fill
              sizes=""
              priority
              className={clsx('object-cover', styles['mask'], styles['mask-8'])}
            />
          </li>
        ))}
      </ul>
      <div className="absolute bottom-4 left-1/2 flex space-x-1.5 md:left-[calc((min(840px,50dvw-40px))/2)] md:-translate-x-1/2">
        {images.map((_, index) => (
          <div
            key={index}
            className={clsx('rounded-full transition-all', {
              'size-2 bg-white/60': selectedIndex !== index,
              'h-2 w-3 bg-white': selectedIndex === index,
            })}
          />
        ))}
      </div>
    </div>
  )
}
